import React, { useEffect, useRef, useState } from "react";

// Pinch / double-tap / wheel zoom + drag-to-pan for the fullscreen photo viewer.
// Applies the transform imperatively (ref) for smooth gestures; state only drives
// the cursor + pan affordance. Remount (key=src) resets zoom on photo change.
// `onSwipe(±1)`: horizontal swipe while NOT zoomed → previous / next photo.
// `onDragDown(dy)` / `onDragEnd(dy, ms)` : glissement vertical vers le bas (non zoomé) → la visionneuse
// suit le doigt puis se ferme (façon Instagram). L'image elle-même ne bouge pas : c'est le parent qui anime.
export const ZoomableImage = ({ src, alt = "", className = "", testId = "zoomable-image", onSwipe, onDragDown, onDragEnd }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const view = useRef({ s: 1, x: 0, y: 0 });
  const pointers = useRef(new Map());
  const gesture = useRef(null); // pinch: {dist, s0, x0, y0, mx, my} | pan: {x, y}
  const lastTap = useRef(0);
  const [zoomed, setZoomed] = useState(false);

  const MIN = 1, MAX = 5;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  const apply = (animate = false) => {
    const el = imgRef.current;
    if (el) {
      // Double tap / molette : zoom animé façon Instagram ; pincement / glissement : suivi immédiat du doigt.
      el.style.transition = animate ? "transform .28s cubic-bezier(.2,.8,.2,1)" : "transform .05s linear";
      el.style.transform = `translate3d(${view.current.x}px, ${view.current.y}px, 0) scale(${view.current.s})`;
    }
    const z = view.current.s > 1.01;
    setZoomed((prev) => (prev !== z ? z : prev));
  };
  const clampPan = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxX = (r.width * (view.current.s - 1)) / 2;
    const maxY = (r.height * (view.current.s - 1)) / 2;
    view.current.x = clamp(view.current.x, -maxX, maxX);
    view.current.y = clamp(view.current.y, -maxY, maxY);
  };
  // Zoom toward a screen point, keeping that point of the image stationary.
  const zoomTo = (clientX, clientY, nextS, animate = false) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = clientX - r.left - r.width / 2;
    const cy = clientY - r.top - r.height / 2;
    const s0 = view.current.s;
    const s1 = clamp(nextS, MIN, MAX);
    const ratio = s1 / s0;
    view.current.x = cx * (1 - ratio) + view.current.x * ratio;
    view.current.y = cy * (1 - ratio) + view.current.y * ratio;
    view.current.s = s1;
    if (s1 <= 1.01) { view.current.x = 0; view.current.y = 0; }
    clampPan();
    apply(animate);
  };
  // Non-passive wheel listener so preventDefault() really blocks the page scroll behind.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.18 : 1 / 1.18;
      zoomTo(e.clientX, e.clientY, view.current.s * factor, true);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  const onPointerDown = (e) => {
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    if (pts.length === 2) {
      gesture.current = { type: "pinch", dist: dist(pts[0], pts[1]), s0: view.current.s };
    } else if (pts.length === 1) {
      // Double tap (façon Instagram) : zoom ×2,6 sur le point touché ; un 2e double tap revient à la taille normale.
      const now = Date.now();
      if (now - lastTap.current < 300) {
        lastTap.current = 0;
        zoomTo(e.clientX, e.clientY, view.current.s > 1.01 ? 1 : 2.6, true);
        gesture.current = null;
        return;
      }
      lastTap.current = now;
      gesture.current = view.current.s > 1.01
        ? { type: "pan", x: e.clientX, y: e.clientY }
        : ((onSwipe || onDragDown) ? { type: "swipe", x0: e.clientX, y0: e.clientY, dx: 0, dy: 0, t0: Date.now(), axis: null, touch: e.pointerType !== "mouse" } : null);
    }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.current.values()];
    const g = gesture.current;
    if (g?.type === "pinch" && pts.length === 2) {
      e.stopPropagation();
      const m = mid(pts[0], pts[1]);
      zoomTo(m.x, m.y, g.s0 * (dist(pts[0], pts[1]) / g.dist));
    } else if (g?.type === "pan" && pts.length === 1) {
      e.stopPropagation();
      view.current.x += e.clientX - g.x;
      view.current.y += e.clientY - g.y;
      g.x = e.clientX; g.y = e.clientY;
      clampPan();
      apply();
    } else if (g?.type === "swipe" && pts.length === 1) {
      g.dx = e.clientX - g.x0;
      g.dy = e.clientY - g.y0;
      // L'axe se fige au 1er mouvement net (> 10 px) : horizontal = photo suivante, vertical (vers le bas) = fermer.
      if (!g.axis && (Math.abs(g.dx) > 10 || Math.abs(g.dy) > 10)) g.axis = Math.abs(g.dy) > Math.abs(g.dx) * 1.2 && g.dy > 0 && onDragDown && g.touch ? "y" : (onSwipe ? "x" : null);
      if (g.axis === "y") { onDragDown(Math.max(0, g.dy)); return; }
      // Follow the finger horizontally (unzoomed) as a visual hint of the page change.
      const el = imgRef.current;
      if (g.axis === "x" && el) { el.style.transition = "transform .05s linear"; el.style.transform = `translate3d(${g.dx}px, 0, 0) scale(1)`; }
    }
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    const g = gesture.current;
    if (g?.type === "swipe") {
      const dy = e.clientY - g.y0;
      if (g.axis === "y") onDragEnd?.(Math.max(0, dy), Date.now() - g.t0);
      else if (g.axis === "x" && Math.abs(g.dx) > 60 && Math.abs(g.dx) > Math.abs(dy) * 1.2) onSwipe?.(g.dx < 0 ? 1 : -1);
      else apply();
    }
    const pts = [...pointers.current.values()];
    if (pts.length === 1) gesture.current = { type: "pan", x: pts[0].x, y: pts[0].y };
    else if (pts.length === 0) gesture.current = null;
  };

  return (
    <div
      ref={wrapRef}
      className="relative flex items-center justify-center overflow-hidden max-h-full max-w-full w-full h-full"
      style={{ touchAction: "none", cursor: zoomed ? "grab" : "zoom-in" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid={testId}
      data-zoomed={zoomed ? "1" : "0"}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        className={`max-h-full max-w-full w-auto h-auto object-contain rounded-lg select-none ${className}`}
        style={{ transformOrigin: "center center", transition: "transform .05s linear", willChange: "transform" }}
        data-testid={`${testId}-img`}
      />
    </div>
  );
};
